import { message } from "antd";
import axios from "axios";
import { axiosInstance } from "@/api/axios-instance";
import * as LocalStorage from "@/storages/LocalStorage";
import { createExpiredToken } from "./tokenUtils";

// Test API request
export const testApiRequest = async () => {
  try {
    // send a request that requires authentication
    // Use a valid health check endpoint instead of /api/health
    const response = await axiosInstance.get("/api/outpatient/health");

    console.log("API response:", response.data);

    return true;
  } catch (error) {
    message.error(`API request failed: ${error}`);
    console.error("API request error:", error);
    return false;
  }
};

// Full test flow: set expired token and send API request directly
export const testFullRefreshFlow = async () => {
  try {
    message.info("starting full test for token refresh...");

    // 1. save the current token
    const originalToken = LocalStorage.getAccessToken();

    // 2. set an expired token
    const expiredToken = createExpiredToken();
    LocalStorage.setAccessToken(expiredToken);
    message.info("expired token set");

    // 3. send API request directly (without manually refreshing the token)
    const success = await testApiRequest();

    // 4. check the result
    if (success) {
      message.success(
        "token refresh test successful! API request completed and token automatically refreshed",
      );
    } else {
      message.error("token refresh test failed");
      // restore the original token
      if (originalToken) {
        LocalStorage.setAccessToken(originalToken);
        message.info("original token restored");
      }
    }
  } catch (error) {
    message.error(`error occurred during test: ${error}`);
    console.error("test error:", error);
  }
};

// Interface for Cognito user attributes
export interface CognitoUserAttribute {
  Name: string;
  Value: string;
}

export interface CognitoUserResponse {
  Username: string;
  UserAttributes: CognitoUserAttribute[];
  [key: string]: unknown;
}

// Test AWS Cognito GetUser API
export const testCognitoGetUser = async (): Promise<CognitoUserResponse | null> => {
  try {
    message.info("Testing AWS Cognito GetUser API...");

    // Get the current access token
    const accessToken = LocalStorage.getAccessToken();

    if (!accessToken) {
      message.error("No access token available. Please login first.");
      return null;
    }

    // Call the Cognito GetUser API
    const response = await axios({
      method: "post",
      url: "https://cognito-idp.ap-southeast-1.amazonaws.com",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": "AWSCognitoIdentityProviderService.GetUser",
      },
      data: {
        AccessToken: accessToken,
      },
    });

    console.log("Cognito GetUser response:", response.data);
    message.success("Cognito GetUser API call successful!");

    // Display user attributes in a more readable format
    if (response.data.UserAttributes) {
      const userInfo = response.data.UserAttributes.reduce(
        (acc: Record<string, string>, attr: CognitoUserAttribute) => {
          acc[attr.Name] = attr.Value;
          return acc;
        },
        {},
      );
      console.log("User attributes:", userInfo);
    }

    return response.data as CognitoUserResponse;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    message.error(`Cognito GetUser API failed: ${errorMessage}`);
    console.error("Cognito GetUser error:", error);
    return null;
  }
};
